import { getAnalytics, logEvent, setUserProperties } from "firebase/analytics";
import { analytics } from "./firebase";
import {
  EMAIL_USER_TYPE,
  ONBOARDING_USER_STATUS,
  APPROVED_USER_STATUS,
  ADMIN_USER_TYPE,
} from "./constants";
import axiosInstance, { PROD_BD_API_URL } from "../api/axiosInstance";

// Extract page and screen from URL
export const getPageAndScreenFromUrl = () => {
  const pathname = window.location.pathname;
  const pathParts = pathname.split("/").filter((part) => part !== "");

  const page = pathParts[0] || "home";
  const screen = pathParts[1] || page;

  return { page, screen };
};

// Get category based on user status
export const getCategoryByUserStatus = (userStatus) => {
  if (userStatus === ONBOARDING_USER_STATUS) {
    return "Onboarding";
  } else if (userStatus === APPROVED_USER_STATUS) {
    return "Dashboard";
  } else {
    return "General"; // fallback for other statuses
  }
};

export const triggerGa = ({
  category = "",
  action = "",
  label = "",
  label2 = "",
  label3 = "",
  page = "",
  screenName = "",
  eventValue = 0,
}) => {
  const userType =
    localStorage.getItem("user_type") || sessionStorage.getItem("user_type");
  const brandId = sessionStorage.getItem("brandId");
  if (userType === ADMIN_USER_TYPE) {
    return;
  }
  if (String(brandId) === "155") {
    return;
  }
  if (
    (userType && userType !== EMAIL_USER_TYPE) ||
    axiosInstance.getUri() !== PROD_BD_API_URL
  ) {
    console.log("GA not triggered");
    return;
  }
  const eventDetails = {
    event_category: category,
    event_action: action,
    event_label: label,
    event_location: window.location.pathname,
    event_label2: label2,
    event_label3: label3,
    page_title: page,
    screen_name: screenName,
    value: eventValue,
  };
  logEvent(analytics, action, eventDetails);
};

export const setUserPropertiesGa = (property_params = {}) => {
  const analytics = getAnalytics();
  setUserProperties(analytics, property_params);
};

export const ACTION = {
  BUTTON_CLICK: "button_click",
  FILTER_INTERACTION: "filter_interaction",
  VIEW_TOGGLE: "view_toggle",
  SORT_SELECT: "sort_select",
  RESULTS_LOADED_NUM: "results_loaded_num",
  NO_MORE_RESULTS: "no_more_results",
  SEARCH: "search",
  MEDIA_PLAY: "media_play",
  PROFILE_CLICK: "profile_click",
  ADD_TO_WISHLIST: "addtowishlist_click",
  CONTACT_CLICK: "contact_click",
  IG_CLICK: "ig_click",
  RECENT_POST_CLICK: "recent_post_click",
  CONTENT_CLICK: "content_click",
  POPULAR_AFFILIATE_CLICK: "popular_affiliate_click",
  PROFILE_CLOSE: "profile_closed_click",
  FILTER_BAR_CLICK: "filter_bar_click",
  FILTER_VALUE_CLICK: "filter_value_click",
  ADVANCED_FILTER_CLICK: "advanced_filter_click",
  SORT_CLICK: "sort_click",
  SORT_VALUE_CLICK: "sort_value_click",
  SEARCH_BAR_CLICK: "search_bar_click",
  SEARCH_SUB_INTEREST_CLICK: "search_sub_interest_click",
  SEARCH_INTEREST_CLICK: "search_interest_click",
  SEARCH_VIEW_RESULTS_CLICK: "search_view_results_click",
  CLEAR_FILTERS_CLICK: "clear_filters_click",
  CLEAR_INTEREST_CLICK: "clear_interest_click",
  CLEAR_SUB_INTEREST_CLICK: "clear_sub_interest_click",
  SEARCH_BACK_CLICK: "search_back_click",
  SEARCH_INTEREST_REMOVE_CLICK: "search_interest_remove_click",
};

export const CATEGORY = {
  NAVIGATION: "navigation",
  CREATOR_DISCOVERY: "creator_discovery",
  CHECK_ANY_CREATOR_STATS: "check_any_creator_stats",
  USER_INTERACTION: "user_interaction",
  PAGE_VIEW: "page_view",
  FORM_ERROR: "form_error",
  FORM_SUCCESS: "form_success",
  USER_REDIRECTION: "user_redirection",
};

export const LABEL = {
  CHAT: "chat",
  DISCOVER: "discover",
  WHISHLISTS: "wishlists",
  CREATOR_INPUTS: "creator_inputs",
  PRODUCT_AFFINITY: "product_affinity",
  SOCIAL_PERFORMANCE: "social_performance",
  AUDIENCE_DEMO: "audience_demo",
  RESET_FILTERS: "reset_filters",
  REMOVE_FILTER: "remove_filter",
  PROFILE_CLICK: "profile_click",
  ADD_TO_WISHLIST: "add_to_wishlist",
  CREATE_NEW_WISHLIST: "create_new_wishlist",
  OPEN_WISHLIST: "open_wishlist",
  EDIT_WISHLIST: "edit_wishlist",
  DELETE_WISHLIST: "delete_wishlist",
  EXPORT_WISHLIST: "export_wishlist",
  TOP_3: "top_3",
  RECENT_CONTENT: "recent_content",
  CREATOR_PROFILE: "creator_profile",
  PRODUCT_BRANDS: "product_brands",
};

export const LABEL2 = {
  ATTEMPT: "attempt",
  ADDED_TO_EXISTING: "added_to_existing",
  ADDED_TO_NEW: "added_to_new",
  PROFILE_INTEREST: "profile_interest",
  SHORTLIST: "shortlist",
};

export const PAGE = {
  CREATORS: "Creators",
  PROFILE: "Profile",
  SIMILAR_PROFILE: "Similar Profile",
  DISCOVER: "Discover",
  WISHLISTS: "Wishlists",
  CREATOR_DISCOVER: "creator/discover",
  /** Brand dashboard — Tracking (matches path segment `tracking`) */
  TRACKING: "tracking",
  TRACKING_CREATE: "tracking/create",
  HELP: "help",
  /** Campaign request form and its details page */
  CAMPAIGN_REQUEST: "campaign/request",
  HOME: "home",
  ANNOUNCEMENTS: "announcements",
  SUGGESTED_CREATORS: "creators/suggested",
};

/** Campaign-scoped page id for analytics: `tracking/{campaignId}` */
export const pageTrackingCampaign = (campaignId) => {
  if (campaignId == null || campaignId === "") return PAGE.TRACKING;
  return `tracking/${String(campaignId)}`;
};

/** Edit flow page id: `tracking/{campaignId}/edit` */
export const pageTrackingCampaignEdit = (campaignId) => {
  if (campaignId == null || campaignId === "") return PAGE.TRACKING_CREATE;
  return `tracking/${String(campaignId)}/edit`;
};

export const SCREEN = {
  DISCOVER: "Discover",
  PROFILE: "Profile",
  SIMILAR_PROFILE: "Similar Profile",
  SIMILAR_CREATORS: "Similar Creators",
  WISHLISTS: "Wishlists",
  CREATOR_DRAWER: "Creator Drawer",
  DISCOVER_LIST: "Discover List",
  DISCOVER_CARD: "Discover Card",
  LANDING: "Landing",
  /** Tracking — campaign list */
  TRACKING_LANDING: "landing",
  TRACKING_LANDING_EMPTY: "landing_empty",
  CAMPAIGN_CREATION: "campaign_creation",
  EDIT_CAMPAIGN: "edit_campaign",
  CAMPAIGN_DETAIL: "campaign_detail",
  CAMPAIGN_LOADING: "campaign_loading",
  CONTENTWISE_FUNNEL: "contentwise_funnel",
  CONTENT_DRAWER: "content_drawer",
  POST_PREVIEW: "post_preview",
  UPLOAD_MODAL: "upload_modal",
  ADD_SKU_MODAL: "add_sku_modal",
  /** Campaign execution `/campaign/execution/:id` — creators + filters */
  EXECUTION_SUMMARY: "execution_summary",
  /** Execution tab — aggregated campaign timeline */
  EXECUTION_TIMELINE_AGGREGATE: "execution_timeline_aggregate",
  /** Right-side creator drawer (timeline + submissions) */
  CREATOR_TIMELINE_DRAWER: "creator_timeline_drawer",
  /** Execution landing — table of execution-visible campaigns only */
  EXECUTION_CAMPAIGN_LIST: "execution_campaign_list",
  HELP: "help",
  DELETE_DIALOG: "delete_dialog",
  CAMPAIGN_REQUEST_FORM: "campaign_request_form",
  CAMPAIGN_REQUEST_DETAIL: "campaign_request_detail",
  CAMPAIGN_REQUEST_WISHLIST_PICKER: "campaign_request_wishlist_picker",
  SUGGESTED_CREATORS: "suggested_creators",
  BRAND_PROFILE: "brand_profile",
  /** Campaign list, Requested tab */
  REQUESTED_CAMPAIGN_LIST: "requested_campaign_list",
  HOME: "home",
  SETUP_CHECKLIST: "setup_checklist",
  ANNOUNCEMENTS: "announcements",
  TOP_BAR: "top_bar",
};
